/**
 * WorkerSchedules — per-worker schedule overrides.
 *
 * Migrated to live API in Wave 3.2. Reads /v1/attendance/schedules/workers
 * plus the parallel reference data needed to resolve FK ids to names:
 * workers, timetables, shift cycles, departments.
 *
 * The backend stores these as append-only Schedule rows with
 * worker_id set (and type != 'temporary'). The most-recent active
 * row per worker is the "current" override. Creating an override
 * just PUTs a new row — no upsert at the service level.
 *
 * Delete hits DELETE /v1/attendance/schedules/workers/{schedule_id},
 * not /workers/{emp_id} — the backend deletes the Schedule row itself
 * so the worker falls back to their department's default.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, CloseOutlined, DeleteOutlined, EditOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type {
  WorkerScheduleAssign, Schedule, ShiftCycle, Timetable,
} from '../../../api/attendance';
import type {
  DepartmentNode, WorkerResponse,
} from '../../../api/organization';
import { ConfirmDialog } from '../../../components/primitives';
import { useTranslation } from 'react-i18next';

import DateField from '../../../components/inputs/DateField';
import { translateApiError } from '../../../i18n/translateApiError';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || '•';
}

function fullName(e: WorkerResponse): string {
  return `${e.first_name} ${e.last_name}`.trim();
}

// ---------------------------------------------------------------------------
// Row shape for the display table
// ---------------------------------------------------------------------------

interface OverrideRow {
  schedule: Schedule;
  worker?: WorkerResponse;
  workerName: string;
  department: string;
  deptDefault: string;      // resolved default schedule name
  overrideLabel: string;    // resolved override schedule name
  overrideKind: 'fixed' | 'cycle' | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WorkerSchedules: React.FC = () => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [deptSchedules, setDeptSchedules] = useState<Schedule[]>([]);
  const [workers, setWorkers] = useState<WorkerResponse[]>([]);
  const [timetables, setTimetables] = useState<Timetable[]>([]);
  const [cycles, setCycles] = useState<ShiftCycle[]>([]);
  const [depts, setDepts] = useState<DepartmentNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OverrideRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    worker_id: string;
    kind: 'fixed' | 'cycle';
    timetable_id: string;
    shift_cycle_id: string;
    effective_from: string;
    auto_shift: boolean;
  }>({
    worker_id: '',
    kind: 'fixed',
    timetable_id: '',
    shift_cycle_id: '',
    effective_from: new Date().toISOString().slice(0, 10),
    auto_shift: false,
  });
  const [pendingRemove, setPendingRemove] = useState<OverrideRow | null>(null);

  // ---------- data ----------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [empSch, deptSch, workers, tts, cycs, ds] = await Promise.all([
        attendanceApi.schedulesApi.listWorkerSchedules(),
        attendanceApi.schedulesApi.listDepartmentSchedules(),
        organizationApi.workersApi.list('active'),
        attendanceApi.timetablesApi.list(),
        attendanceApi.shiftCyclesApi.list(),
        organizationApi.departmentsApi.list(),
      ]);
      setSchedules(empSch);
      setDeptSchedules(deptSch);
      setWorkers(workers);
      setTimetables(tts);
      setCycles(cycs);
      setDepts(ds);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:workerSchedules.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---------- lookups ----------
  const workerById = useMemo(
    () => new Map(workers.map((e) => [e.id, e])),
    [workers],
  );
  const deptMap = useMemo(() => {
    const m = new Map<string, string>();
    const walk = (nodes: DepartmentNode[]) => {
      for (const d of nodes) {
        m.set(d.id, d.name);
        if (d.children && d.children.length) walk(d.children);
      }
    };
    walk(depts);
    return m;
  }, [depts]);
  const timetableById = useMemo(
    () => new Map(timetables.map((t) => [t.id, t])),
    [timetables],
  );
  const cycleById = useMemo(
    () => new Map(cycles.map((c) => [c.id, c])),
    [cycles],
  );

  // Department's active default per dept id (same logic as DepartmentSchedules).
  const deptActiveByDept = useMemo(() => {
    const map = new Map<string, Schedule>();
    for (const s of deptSchedules) {
      if (!s.department_id || s.status !== 'active') continue;
      const prev = map.get(s.department_id);
      if (!prev || new Date(s.created_at) > new Date(prev.created_at)) {
        map.set(s.department_id, s);
      }
    }
    return map;
  }, [deptSchedules]);

  const resolveLabel = useCallback((
    s: Schedule | undefined,
    tts: Map<string, Timetable>,
    cs: Map<string, ShiftCycle>,
  ): { label: string; kind: 'fixed' | 'cycle' | null } => {
    if (!s) return { label: t('attendance:workerSchedules.colDeptDefault'), kind: null };
    if (s.timetable_id && tts.has(s.timetable_id)) {
      return { label: tts.get(s.timetable_id)!.name, kind: 'fixed' };
    }
    if (s.shift_cycle_id && cs.has(s.shift_cycle_id)) {
      return { label: cs.get(s.shift_cycle_id)!.name, kind: 'cycle' };
    }
    return { label: s.type === 'cycle' ? t('attendance:workerSchedules.cycleSchedule') : t('attendance:workerSchedules.fixedSchedule'), kind: s.type === 'cycle' ? 'cycle' : 'fixed' };
  }, [t]);

  const rows: OverrideRow[] = useMemo(() => {
    // Take most-recent active per worker.
    const latest = new Map<string, Schedule>();
    for (const s of schedules) {
      if (!s.worker_id || s.status !== 'active') continue;
      const prev = latest.get(s.worker_id);
      if (!prev || new Date(s.created_at) > new Date(prev.created_at)) {
        latest.set(s.worker_id, s);
      }
    }
    const list: OverrideRow[] = [];
    Array.from(latest.entries()).forEach(([empId, sch]) => {
      const worker = workerById.get(empId);
      const deptName = worker?.primary_department_id
        ? deptMap.get(worker.primary_department_id) ?? '—'
        : '—';
      const deptDefault = worker?.primary_department_id
        ? resolveLabel(deptActiveByDept.get(worker.primary_department_id), timetableById, cycleById).label
        : t('attendance:workerSchedules.colDeptDefault');
      const { label, kind } = resolveLabel(sch, timetableById, cycleById);
      list.push({
        schedule: sch,
        worker: worker,
        workerName: worker ? fullName(worker) : `Worker ${empId}`,
        department: deptName,
        deptDefault,
        overrideLabel: label,
        overrideKind: kind,
      });
    });
    return list;
  }, [schedules, workerById, deptMap, deptActiveByDept, timetableById, cycleById, resolveLabel, t]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.workerName.toLowerCase().includes(q) ||
        r.department.toLowerCase().includes(q),
    );
  }, [rows, filter]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // ---------- mutations ----------
  const handleOpenAdd = () => {
    setEditing(null);
    setForm({
      worker_id: '',
      kind: 'fixed',
      timetable_id: '',
      shift_cycle_id: '',
      effective_from: new Date().toISOString().slice(0, 10),
      auto_shift: false,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row: OverrideRow) => {
    setEditing(row);
    setForm({
      worker_id: row.schedule.worker_id ?? '',
      kind: row.overrideKind ?? 'fixed',
      timetable_id: row.schedule.timetable_id ?? '',
      shift_cycle_id: row.schedule.shift_cycle_id ?? '',
      effective_from:
        (row.schedule.effective_from as string | undefined) ??
        new Date().toISOString().slice(0, 10),
      auto_shift: row.schedule.auto_shift ?? false,
    });
    setDialogOpen(true);
  };

  const canSave =
    !!form.worker_id &&
    ((form.kind === 'fixed' && !!form.timetable_id) ||
      (form.kind === 'cycle' && !!form.shift_cycle_id)) &&
    !!form.effective_from;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload: WorkerScheduleAssign = {
        type: form.kind,
        effective_from: form.effective_from,
        auto_shift: form.auto_shift,
        timetable_id: form.kind === 'fixed' ? form.timetable_id : null,
        shift_cycle_id: form.kind === 'cycle' ? form.shift_cycle_id : null,
      };
      await attendanceApi.schedulesApi.assignWorkerSchedule(form.worker_id, payload);
      setToast(editing ? t('attendance:workerSchedules.toastOverrideUpdated') : t('attendance:workerSchedules.toastOverrideCreated'));
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemove) return;
    try {
      await attendanceApi.schedulesApi.removeWorkerSchedule(pendingRemove.schedule.id);
      setToast(t('attendance:workerSchedules.toastOverrideRemoved', { name: pendingRemove.workerName }));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:workerSchedules.removeFailed'));
      throw e;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:workerSchedules.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:workerSchedules.subtitle')}</Typography>
          {/* How this layer interacts with the others.
              Same shape the Rules and Geofences pages already use — a second line of
              small text under the subtitle, not a diagram or a dialog. Added 2026-08-10
              on owner review: those two modules explain their override order and the
              three schedule pages explained nothing, while the schedule chain is the one
              with the surprises. Each sentence answers a specific way an admin gets it
              wrong, and no page repeats the whole chain (two schedules on one worker resolve by latest start date rather than raising a conflict). */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {t('attendance:workerSchedules.precedence')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={handleOpenAdd}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
            {t('attendance:workerSchedules.addOverride')}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" variant="outlined" label={t("attendance:workerSchedules.filterLabel")} value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(0); }}
          sx={{ width: 400, maxWidth: '100%', mr: 2 }} />
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]} />
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t("attendance:workerSchedules.colWorker")}</TableCell>
              <TableCell>{t("attendance:workerSchedules.colDeptDefault")}</TableCell>
              <TableCell>{t("attendance:workerSchedules.colOverride")}</TableCell>
              <TableCell>{t("attendance:workerSchedules.colEffective")}</TableCell>
              <TableCell align="center">{t("attendance:workerSchedules.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <Typography variant="body2">{t('attendance:workerSchedules.noOverrides')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((o) => (
                <TableRow key={o.schedule.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={o.worker?.avatar_url ?? undefined}
                        sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                        {initials(o.workerName)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {o.workerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{o.department}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{o.deptDefault}</Typography>
                  </TableCell>
                  <TableCell>{o.overrideLabel}</TableCell>
                  <TableCell>{o.schedule.effective_from as string | undefined}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenEdit(o)}>
                      <EditOutlined fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title={t('attendance:workerSchedules.removeOverrideTooltip')}><IconButton size="small" onClick={() => setPendingRemove(o)}>
                      <DeleteOutlined fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        {t('attendance:workerSchedules.workersWithoutOverridesPrefix')}{' '}
        <Typography component="span" variant="caption"
          sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => (window.location.href = '/attendance/schedules/department')}>{t('attendance:workerSchedules.departmentScheduleLink')}</Typography>.
        {' '}{t('attendance:workerSchedules.dontSeeWorker')}{' '}
        <Typography component="span" variant="caption"
          sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => (window.location.href = '/organization/workers/active')}>{t("attendance:workerSchedules.manageWorkers")}</Typography>
      </Typography>

      {/* Edit / add dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ m: 0, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>
            {editing ? t('attendance:workerSchedules.editOverride') : t('attendance:workerSchedules.addOverride')}
          </Typography>
          <IconButton size="small" onClick={() => !saving && setDialogOpen(false)}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small" disabled={!!editing || workers.length === 0}>
              <InputLabel>{t("attendance:workerSchedules.workerLabel")}</InputLabel>
              <Select value={form.worker_id} label={t("attendance:workerSchedules.workerLabel")}
                onChange={(e) => setForm((f) => ({ ...f, worker_id: e.target.value }))}>
                {workers.map((worker) => (
                  <MenuItem key={worker.id} value={worker.id}>{fullName(worker)} ({worker.worker_code})</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>{t("attendance:workerSchedules.scheduleKindLabel")}</InputLabel>
              <Select value={form.kind} label={t("attendance:workerSchedules.scheduleKindLabel")}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as 'fixed' | 'cycle' }))}>
                <MenuItem value="fixed">{t("attendance:workerSchedules.fixedTimetable")}</MenuItem>
                <MenuItem value="cycle">{t("attendance:workerSchedules.shiftCycle")}</MenuItem>
              </Select>
            </FormControl>

            {form.kind === 'fixed' ? (
              <FormControl fullWidth size="small" disabled={timetables.length === 0}>
                <InputLabel>{t("attendance:workerSchedules.timetableLabel")}</InputLabel>
                <Select value={form.timetable_id} label={t("attendance:workerSchedules.timetableLabel")}
                  onChange={(e) => setForm((f) => ({ ...f, timetable_id: e.target.value }))}>
                  {timetables.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth size="small" disabled={cycles.length === 0}>
                <InputLabel>{t("attendance:workerSchedules.shiftCycleLabel")}</InputLabel>
                <Select value={form.shift_cycle_id} label={t("attendance:workerSchedules.shiftCycleLabel")}
                  onChange={(e) => setForm((f) => ({ ...f, shift_cycle_id: e.target.value }))}>
                  {cycles.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}

            <DateField fullWidth size="small" label={t("attendance:workerSchedules.effectiveFromLabel")}
              value={form.effective_from}
              onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button size="small" onClick={() => setDialogOpen(false)} disabled={saving}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t("common:actions.cancel")}</Button>
          <Button variant="contained" size="small" onClick={handleSave} disabled={!canSave || saving}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
            {saving ? <CircularProgress size={18} /> : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingRemove}
        tone="warning"
        title={t('attendance:workerSchedules.removeOverrideTitle')}
        description="The worker will revert to their department's default schedule immediately. This cannot be undone — create a new override to re-apply."
        subject={pendingRemove && (
          <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{pendingRemove.workerName}</Box>
        )}
        confirmLabel={t('common:actions.remove')}
        confirmLoadingLabel={t('common:status.removing')}
        onConfirm={handleConfirmRemove}
        onClose={() => setPendingRemove(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkerSchedules;
